import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { HisBackendService } from '../../core/service/backend/his-backend.service';
import { Dimension } from '../model/dimension';
import { FactTable } from '../model/fact-table';
import { Measure } from '../model/measure';
import { ReportDefinition } from '../model/report-definition';
import { Filter } from '../model/filter';
import { Sachgebiet } from '../model/sachgebiet';
import { StoredReport, StoredReportRequest } from '../model/stored-report';
import { Result } from '../component/bi-analysis-result-view/bi-analysis-table/flat-table/model/result';
import { Download } from '../model/download';


@Injectable( {
    providedIn: 'root',
} )

export class BiAnalysisService {
    private hisBackendService = inject( HisBackendService );

    private factTablesCache$?: Observable<FactTable[]>;
    private orderedFactTablesCache$?: Observable<FactTable[]>;
    private orderedSachgebieteCache$?: Observable<Sachgebiet[]>;

    getFactTables(): Observable<FactTable[]> {
        if ( !this.factTablesCache$ ) {
            this.factTablesCache$ = this.hisBackendService
                .getData( '../../superx/ds/api/bianalysis/facttables' )
                .pipe(
                    map( list =>
                        list.map( ( obj: FactTable ) => FactTable.createFromObject( obj ) ),
                    ),
                    tap( list =>
                        console.log( 'Got list of fact tables from server', list, ),
                    ),
                    shareReplay( { bufferSize: 1, refCount: false, } ),
                );
        }

        return this.factTablesCache$;
    }

    getOrderedFactTables(): Observable<FactTable[]> {
        if ( !this.orderedFactTablesCache$ ) {
            this.orderedFactTablesCache$ = this.getFactTables().pipe(
                map( factTables =>
                    [...factTables].sort( ( a, b ) =>
                        ( a.caption ?? '' ).localeCompare( b.caption ?? '' ),
                    ),
                ),
                shareReplay( { bufferSize: 1, refCount: false, } ),
            );
        }
        return this.orderedFactTablesCache$;
    }

    getOrderedSachgebiete(): Observable<Sachgebiet[]> {
        if ( !this.orderedSachgebieteCache$ ) {
            this.orderedSachgebieteCache$ =
                this.getOrderedFactTables().pipe(
                    map( factTables => {
                        const sachgebieteById = new Map<number, Sachgebiet>();
                        for ( const factTable of factTables ) {
                            const sachgebiet = factTable.sachgebiet;
                            const tid = sachgebiet?.tid;
                            const name = sachgebiet?.name?.trim();
                            if ( tid != null && name != null && !sachgebieteById.has( tid ) ) {
                                sachgebieteById.set( tid, { ...sachgebiet, tid, name, } );
                            }
                        }
                        return [...sachgebieteById.values()].sort( ( a, b ) => ( a.name ?? '' ).localeCompare( b.name ?? '', ), );
                    } ),
                    shareReplay( { bufferSize: 1, refCount: false, } ),
                );
        }

        return this.orderedSachgebieteCache$;
    }

    getDimensions( factTableId: string ): Observable<Dimension[]> {
        return this.hisBackendService.getData( '../../superx/ds/api/bianalysis/dimensions?facttable_id=' + factTableId ).pipe(
            tap( list => console.log( 'Got list of dimensions from server ', list ) )
        );
    }

    getRolePlayingDimensions( factTableId: string, secondaryFactTableId: string ): Observable<Dimension[]> {
        return this.hisBackendService.getData( '../../superx/ds/api/bianalysis/rolePlayingDimensions?primaryFactId=' + factTableId + '&secondaryFactId=' + secondaryFactTableId ).pipe(
            tap( list => console.log( 'Got list of role playing dimensions from server ', list ) )
        );
    }

    getMeasures( factTableId: string ): Observable<Measure[]> {
        return this.hisBackendService.getData( '../../superx/ds/api/bianalysis/measures?facttable_id=' + factTableId ).pipe(
            tap( list => console.log( 'Got list of measures from server ', list ) )
        );
    }

    getReportData( reportDefinition: ReportDefinition ): Observable<Result> {
        return this.hisBackendService.postData( '../../superx/ds/api/bianalysis/report', reportDefinition )
            .pipe(
                tap( report => console.log( 'Got report from server ', report ) )
            );
    }

    getDimensionAttributeValues( attributeIds: string[], factIds: string[] ): Observable<string[]> {
        return this.hisBackendService.getData(
            '../../superx/ds/api/bianalysis/dimensionAttributeValues?attribute_id=' + attributeIds + '&facts=' + factIds )
            .pipe(
                tap( values => console.log( 'Got dimension attribute values from server ', values ) )
            );
    }

    getDimensionAttributeValuesHierarchy( attributeId: string ): Observable<any[][]> {
        return this.hisBackendService.getData(
            '../../superx/ds/api/bianalysis/dimensionAttributeValuesHierarchy?attribute_id=' + attributeId )
            .pipe(
                tap( values => console.log( 'Got dimension attribute values hierarchy from server ', values ) )
            );
    }

    getStoredReport( storedReportRequest: StoredReportRequest ): Observable<StoredReport> {
        return this.hisBackendService.postData( '../../superx/ds/api/bianalysis/getStoredReport', storedReportRequest );
    }

    findPersistedReportData( title: string, sach?: number, facts?: string[] ): Observable<StoredReport[]> {
        let url = '?';
        if ( title ) {
            url += 'title=' + title + '&';
        }
        if ( sach != undefined && sach >= 0 ) {
            url += 'sach=' + sach + '&';
        }
        if ( facts && facts.length > 0 ) {
            url += 'facts=' + facts + '&';
        }
        url = url.substring( 0, url.length - 1 );
        return this.hisBackendService.getData( '../../superx/ds/api/bianalysis/findReportDefinition' + url );
    }

    persistReportDefinition( storedReport: StoredReport ): Observable<number> {
        return this.hisBackendService.postData( '../../superx/ds/api/bianalysis/persistReportDefinition', storedReport ).pipe(
            tap( id => console.log( 'Got new id from report definition ', id ) )
        );
    }

    getReportDefinitions(): Observable<StoredReport[]> {
        return this.hisBackendService.getData( '../../superx/ds/api/bianalysis/reportDefinitions' ).pipe(
            tap( list => console.log( 'Got list of report definitions from server ', list ) )
        );
    }

    deleteReportDefinition( reportDefinitionID: number ): Observable<boolean> {
        return this.hisBackendService.postData( '../../superx/ds/api/bianalysis/deleteReportDefinition', reportDefinitionID ).pipe(
            tap( success => console.log( 'Got boolean  ', success ) )
        );
    }

    downloadExcelFile( storedReport: StoredReport ): Observable<HTMLAnchorElement> {
        return this.hisBackendService.postData( '../../superx/ds/api/bianalysis/report/download', storedReport )
            .pipe(
                map( ( data: Download ) => {
                    console.log( 'Got download from server', data );
                    return BiAnalysisService.createHtmlAnchorElement( data );
                } )
            );
    }

    downloadJasperReport( storedReportId: number ): Observable<HTMLAnchorElement> {
        return this.hisBackendService.getData( '../../superx/ds/api/bianalysis/jasper/template?stored_report_id=' + storedReportId )
            .pipe(
                map( ( data: Download ) => {
                    console.log( 'Got Jasper Report download from server', data );
                    return BiAnalysisService.createHtmlAnchorElement( data );
                } )
            );
    }

    static createHtmlAnchorElement( data: Download ): HTMLAnchorElement {
        let download = this.extractDocumentfromDownload( data );
        let element = document.createElement( 'a' );
        element.href = download;
        element.type = data.contentType;
        element.download = data.filename;
        element.style.display = 'none';
        return element;
    }

    static extractDocumentfromDownload( data: Download ): string {
        let byteString = atob( data.base64String );
        let ab = new ArrayBuffer( byteString.length );
        let ia = new Uint8Array( ab );
        for ( let i = 0; i < byteString.length; i++ ) {
            ia[i] = byteString.charCodeAt( i );
        }
        let blob = new Blob( [ia.buffer], { type: data.contentType } );
        let download = window.URL.createObjectURL( blob );
        return download;
    }

}
